"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function MeetYourCoach() {
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const stats = [{
    number: "500+",
    label: "Transformations"
  }, {
    number: "8",
    label: "Years Coaching"
  }, {
    number: "Global",
    label: "Online Reach"
  }];
  return <section data-section-id="944" id="meet-your-coach" className="relative bg-background text-foreground py-24 md:py-40 overflow-hidden">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-multiply" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
    }} aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          {}
          <div ref={imageRef} data-index={0} className="lg:col-span-6 lg:-mt-20 transition-all duration-[1400ms] ease-out" style={{
          opacity: imageVisible ? 1 : 0,
          transform: imageVisible ? "translateY(0) rotate(0deg)" : "translateY(40px) rotate(2deg)"
        }}>
            <div className="relative">
              {}
              <div aria-hidden="true" className="absolute -bottom-3 -left-3 w-1/3 h-1/3 border-l border-b pointer-events-none" style={{
              borderColor: "var(--primary)"
            }} />

              <div className="relative aspect-[4/5] w-full overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700144/img/coach_kira_fitness_portrait.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700144/img/coach_kira_fitness_portrait.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700144/img/coach_kira_fitness_portrait.webp-wvc-sizes" alt="Coach Kira, personal fitness trainer, in a high-performance gym setting" loading="lazy" decoding="async" className="h-full w-full object-cover" />
              </div>

              {}
              <p className="mt-6 font-default italic text-[14px] text-muted-foreground tracking-normal">
                — Coach Kira, Founder & Lead Trainer
              </p>
            </div>
          </div>

          {}
          <div ref={textRef} className="lg:col-span-5 lg:col-start-8 flex flex-col justify-center">
            {}
            <div className="transition-all duration-1000 ease-out" style={{
            opacity: textVisible ? 1 : 0,
            transform: textVisible ? "translateY(0)" : "translateY(20px)",
            transitionDelay: "100ms"
          }}>
              <span className="font-default text-xs uppercase tracking-[0.25em] text-muted-foreground">
                The Expert
              </span>
            </div>

            {}
            <h2 className="font-default font-bold text-foreground mt-6 leading-[1.05] tracking-tight transition-all duration-1000 ease-out" style={{
            fontSize: "clamp(3rem, 5vw, 4.5rem)",
            opacity: textVisible ? 1 : 0,
            transform: textVisible ? "translateY(0)" : "translateY(20px)",
            transitionDelay: "250ms"
          }}>
              Fitness through{" "}
              <span className="italic font-normal text-primary">precision.</span>
            </h2>

            {}
            <div className="mt-10 space-y-6 max-w-md transition-all duration-1000 ease-out" style={{
            opacity: textVisible ? 1 : 0,
            transform: textVisible ? "translateY(0)" : "translateY(20px)",
            transitionDelay: "400ms"
          }}>
              <p className="font-default text-muted-foreground" style={{
              fontSize: "18px",
              lineHeight: 1.7
            }}>
                Built on the foundation of functional movement and scientific training, 
                my approach is designed to strip away the guesswork. I believe that 
                sustainable health is not about intensity alone, but about intentional, 
                measurable progress.
              </p>
              <p className="font-default text-muted-foreground" style={{
              fontSize: "18px",
              lineHeight: 1.7
            }}>
                At Coach Kira Fit, we bridge the gap between ambition and reality. 
                Whether you are starting your first program or refining your athletic 
                performance, I provide the structure, motivation, and technical 
                guidance to help you reach your peak.
              </p>
            </div>

            {}
            <div className="mt-12 flex items-stretch gap-6 transition-all duration-1000 ease-out" style={{
            opacity: textVisible ? 1 : 0,
            transform: textVisible ? "translateY(0)" : "translateY(20px)",
            transitionDelay: "550ms"
          }}>
              {stats.map((stat, i) => <div key={i} data-index={i} className="flex items-stretch gap-6">
                  {i > 0 && <Separator orientation="vertical" className="h-auto bg-border" />}
                  <div className="flex flex-col">
                    <span className="font-default text-3xl md:text-4xl font-bold text-foreground leading-none">
                      {stat.number}
                    </span>
                    <span className="font-default text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-2">
                      {stat.label}
                    </span>
                  </div>
                </div>)}
            </div>

            {}
            <div className="mt-12 transition-all duration-1000 ease-out" style={{
            opacity: textVisible ? 1 : 0,
            transform: textVisible ? "translateY(0)" : "translateY(20px)",
            transitionDelay: "700ms"
          }}>
              <Link to="/MeetKiraPage" className="group inline-flex items-center gap-3 font-default text-sm uppercase tracking-[0.2em] text-primary hover:text-primary/80 transition-colors">
                <span>Read my philosophy</span>
                <ArrowRight className="size-4 transition-transform duration-500 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}